//  FolderNode.jsx               // рекурсивний вузол дерева

import PropTypes from 'prop-types';
import React from 'react';

/* ====== Рекурсивний вузол LAZY-дерева ====== */

const isFolder = (node) =>
    ((node?.type || node?.mimeType) === 'application/vnd.google-apps.folder') || node.isShortcutFolder;

// Невелика заглушка-іконка, якщо немає iconLink
const FallbackIcon = ({ mime }) => {
    const isImg = String(mime || '').startsWith('image/');
    const isPdf = mime === 'application/pdf';
    const isDoc = (mime || '').includes('document') || (mime || '').includes('msword');
    const isXls = (mime || '').includes('spreadsheet') || (mime || '').includes('excel');
    const isPpt = (mime || '').includes('presentation') || (mime || '').includes('powerpoint');
    return (
        <span className="inline-flex w-4 h-4 items-center justify-center text-[13px]" aria-hidden>
            {isImg ? '🖼️' : isPdf ? '📄' : isDoc ? '📝' : isXls ? '📊' : isPpt ? '📈' : '📎'}
        </span>
    );
};

function FileRow({ id, name, icon, type, onOpen, nodes }) {
    return (
        <div className="flex items-center gap-2 py-0.5">
            {icon ? (
                <img
                    src={icon}
                    alt=""
                    width={16}
                    height={16}
                    className="inline-block rounded-sm shrink-0"
                    loading="lazy"
                />
            ) : (
                <FallbackIcon mime={type} />
            )}
            <button
                className="flex-1 text-left hover:underline truncate"
                title={name}
                onClick={() => onOpen(id, type, nodes)}
            >
                {name}
            </button>
        </div>
    );
}

function makeNode({ id, name = 'Folder', icon = null, type = null, children = null }) {
    return { id, name, icon, type, children };
}

export default function FolderNode({
    id,
    name = 'Folder',
    tree,
    expanded,
    toggleNode,
    ensureChildren,
    onEnter,
    depth = 0,
    onOpen,
}) {

    const node = tree[id] || makeNode({ id, name });
    const isOpen = !!expanded[id];

    // console.log('node');
    // console.log(node);

    // Ліниве завантаження вмісту папки при відкритті (опціонально)
    const handleToggle = () => {
        toggleNode(id, node.name);
        if (!isOpen && typeof ensureChildren === 'function') {
            ensureChildren(id);
        }
    };

    return (
        <div className="ml-[0px]">
            <div className="flex items-center gap-1">
                <button
                    className="w-5 h-5 flex items-center justify-center text-xs border rounded"
                    onClick={handleToggle}
                    title={isOpen ? 'Згорнути' : 'Розгорнути'}
                >
                    {isOpen ? '−' : '+'}
                </button>

                <button
                    className="flex-1 text-left hover:underline truncate"
                    onClick={() => onEnter({ id, name: node.name })}
                    title={node.name}
                >
                    {(node.isShortcutFolder ? '🔗' : '') + (isOpen ? '📂 ' : '📁 ') + node.name}
                </button>
            </div>

            {isOpen && (
                <div className="pl-4 mt-1">
                    {node.loadingChildren && (
                        <div className="text-xs text-gray-500 italic flex items-center gap-1">
                            <svg className="animate-spin h-3.5 w-3.5 text-gray-400" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                                <path className="opacity-75" d="M4 12a8 8 0 0 1 8-8" stroke="currentColor" strokeWidth="3" />
                            </svg>
                            Йде розгортання…
                        </div>
                    )}

                    {!node.loadingChildren && Array.isArray(node.children) && node.children.length === 0 && (
                        <div className="text-xs text-gray-400">(Порожньо)</div>
                    )}

                    {!node.loadingChildren && node.children === null && (
                        <div className="text-xs text-gray-500">Завантаження…</div>
                    )}
                    {Array.isArray(node.children) &&
                        node.children.map((ch) =>
                            isFolder(ch) ? (
                                <FolderNode
                                    key={ch.id}
                                    id={ch.id}
                                    name={ch.name}
                                    tree={tree}
                                    expanded={expanded}
                                    toggleNode={toggleNode}
                                    ensureChildren={ensureChildren}
                                    onEnter={onEnter}
                                    depth={depth + 1}
                                    onOpen={onOpen}
                                />
                            ) : (
                                <FileRow
                                    key={ch.id}
                                    id={ch.id}
                                    name={ch.name}
                                    icon={ch.icon}
                                    type={ch.type || ch.mimeType}
                                    onOpen={onOpen}
                                    nodes={node.children}
                                />
                            )
                        )}
                </div>
            )}
        </div>
    );
}

FallbackIcon.propTypes = {
    mime: PropTypes.string,
};

FileRow.propTypes = {
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    icon: PropTypes.string,
    type: PropTypes.string,
    onOpen: PropTypes.func.isRequired,
    nodes: PropTypes.object.isRequired,
};

FolderNode.propTypes = {
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    tree: PropTypes.object.isRequired,
    expanded: PropTypes.object.isRequired,
    toggleNode: PropTypes.func.isRequired,
    ensureChildren: PropTypes.func,
    onEnter: PropTypes.func.isRequired,
    depth: PropTypes.number,
    onOpen: PropTypes.func.isRequired,
};

FolderNode.defaultProps = {
    name: "Folder",
    depth: 0,
    onEnter: () => { },
    onOpen: () => { },
    ensureChildren: undefined,
};
